// Metas del periodo, compartidas por Tiendas y Vendedores.
//
// Las dos pantallas piden lo mismo cambiando el `targetType`, y las dos pintan la barra de avance
// contra `dailyGoalCents`. La lógica vive acá y no duplicada en cada `use*.ts` — si el criterio del
// periodo o del merge cambia, cambia en un lugar.
import { useQuery } from '@tanstack/react-query';
import { goalsApi, type GoalTargetType } from '@/api/services/retail-org/backoffice';
import {
    reportPeriodDays,
    scaleMonthlyGoal,
    type ReportPeriod,
} from '@/shared/patterns/PeriodBand';

/**
 * El periodo es el mes calendario en curso, en HORA LOCAL.
 *
 * En UTC, del día 1 antes de las 6 de la mañana de CDMX se pediría el mes ANTERIOR y la pantalla
 * mostraría la meta del mes pasado sin avisar. Es el mismo bug de zona horaria que ya nos mordió en
 * el filtro de fecha de Clientes.
 */
export function currentPeriodId(now: Date = new Date()): string {
    return `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Las metas del periodo, en su PROPIO query.
 *
 * Separado de la lista a propósito: hoy la tabla `SureKeepRetailGoal_GT` no existe y este endpoint
 * falla. Fusionarlo con la lista tumbaría una pantalla que hoy funciona por un dato accesorio.
 * Cuando falla, `dailyGoalCents` se queda en `null` y la UI ya sabe pintar el guion.
 *
 * `retry: false` porque el fallo esperado es «la tabla no existe», que no se arregla reintentando:
 * tres intentos solo retrasan el render de la lista.
 */
export function useGoals(targetType: GoalTargetType, enabled = true) {
    const periodId = currentPeriodId();
    return useQuery({
        queryKey: ['retail-admin', 'goals', periodId, targetType],
        queryFn: () => goalsApi.listGoals(periodId, targetType),
        enabled,
        staleTime: 5 * 60_000,
        retry: false,
    });
}

/**
 * Pega la meta DIARIA a cada fila por su id.
 *
 * El target sin meta conserva su `null`: no se cae a cero. Un `$0` de meta haría que la barra
 * marcara 100% cumplido con una sola venta, que es la lectura más peligrosa posible.
 */
export function withDailyGoal<T extends { dailyGoalCents: number | null }>(
    rows: T[],
    idOf: (row: T) => string,
    goals: { targetId: string; dayCents: number }[] | undefined,
): T[] {
    if (!goals?.length) return rows;
    const byTarget = new Map(goals.map((g) => [g.targetId, g.dayCents]));
    return rows.map((row) => {
        const dayCents = byTarget.get(idOf(row));
        return dayCents === undefined ? row : { ...row, dailyGoalCents: dayCents };
    });
}

/**
 * Pega a cada fila la meta mensual escalada al periodo (`mes * días / 30`, como el mock). En estas
 * pantallas `dailyGoalCents` es la meta del periodo; sin meta o en «Todo el historial» queda `null`.
 */
export function withPeriodGoal<T extends { dailyGoalCents: number | null }>(
    rows: T[],
    idOf: (row: T) => string,
    goals: { targetId: string; monthCents: number }[] | undefined,
    period: ReportPeriod,
): T[] {
    if (!goals?.length || reportPeriodDays(period) === null) return rows;
    const byTarget = new Map(goals.map((g) => [g.targetId, g.monthCents]));
    return rows.map((row) => {
        const monthCents = byTarget.get(idOf(row));
        const goalCents = monthCents === undefined ? null : scaleMonthlyGoal(monthCents, period);
        return goalCents === null ? row : { ...row, dailyGoalCents: goalCents };
    });
}
